'use client';

import React, { useState } from 'react';
import {
  Building2,
  UserRound,
  LogIn,
  ShieldCheck,
  FileText,
  ExternalLink,
  X,
  CheckCircle2,
  ClipboardList,
  Award,
  BookOpen,
} from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';

interface AuthLandingProps {
  onLogin: () => void;
  onCompanyRegister: () => void;
  onEvaluatorRegister: () => void;
}

type ConvocatoriaType = 'empresa' | 'evaluador' | null;

const DOF_COMPANY_URL = '';
const DOF_EVALUATOR_URL = '';

export default function AuthLanding({
  onLogin,
  onCompanyRegister,
  onEvaluatorRegister,
}: AuthLandingProps) {
  const [convocatoria, setConvocatoria] =
    useState<ConvocatoriaType>(null);

  const isCompany = convocatoria === 'empresa';

  return (
    <div className="min-h-screen flex bg-background">

      {/* PANEL INSTITUCIONAL */}
      <div className="hidden lg:flex lg:w-[52%] xl:w-[55%] institution-gradient flex-col justify-between p-12 relative overflow-hidden">

        <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/5 pointer-events-none" />
        <div className="absolute top-1/2 right-16 w-48 h-48 rounded-full bg-secondary/10 pointer-events-none" />

        <div className="relative">

          <div className="flex items-center gap-4 mb-12">
            <AppLogo size={56} />

            <div>
              <p className="text-white font-800 text-xl leading-tight">
                Premio Nacional
              </p>
              <p className="text-white/70 text-base">
                de Exportación · México
              </p>
            </div>
          </div>

          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-secondary/20 border border-secondary/30 mb-6">
            <span className="w-2 h-2 rounded-full bg-secondary animate-pulse" />
            <span className="text-secondary text-sm font-semibold">
              Edición 2026 · En curso
            </span>
          </div>

          <h1 className="text-white font-800 text-4xl xl:text-5xl leading-tight mb-5">
            Plataforma Oficial
            <br />
            de Evaluación
          </h1>

          <p className="text-white/65 text-base leading-relaxed max-w-md">
            Sistema integral para el registro, evaluación y seguimiento
            de las empresas participantes y personas evaluadoras del
            Premio Nacional de Exportación.
          </p>
        </div>

        <div className="relative">
          <div className="flex items-center gap-3 text-white/45 text-xs">
            <ShieldCheck size={16} />
            <span>
              Acceso protegido · Premio Nacional de Exportación · México
            </span>
          </div>
        </div>
      </div>

      {/* PANEL PRINCIPAL */}
      <div className="flex-1 flex items-center justify-center px-6 py-10 sm:px-10 lg:px-16 xl:px-20 bg-background">

        <div className="max-w-xl w-full">

          {/* LOGO MÓVIL */}
          <div className="flex lg:hidden items-center gap-3 mb-10">
            <AppLogo size={44} />

            <div>
              <p className="font-800 text-base text-foreground">
                Premio Nacional de Exportación
              </p>
              <p className="text-xs text-muted-foreground">
                Plataforma Oficial · México
              </p>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-800 text-foreground">
              Bienvenido
            </h2>

            <p className="text-muted-foreground text-sm mt-2">
              Consulta la convocatoria correspondiente o selecciona una
              opción para continuar.
            </p>
          </div>

          {/* CONVOCATORIAS */}
          <div className="grid gap-4 sm:grid-cols-2 mb-8">

            <button
              type="button"
              onClick={() => setConvocatoria('empresa')}
              className="w-full text-left rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary hover:shadow-md group"
            >
              <div className="flex items-start gap-4">

                <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                  <FileText size={22} />
                </div>

                <div className="flex-1">
                  <p className="font-800 text-base text-foreground">
                    Convocatoria para empresas
                  </p>

                  <p className="text-sm text-muted-foreground mt-1">
                    Consulta requisitos, proceso de participación y bases.
                  </p>

                  <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-700 text-primary">
                    Consultar convocatoria
                    <ExternalLink size={13} />
                  </span>
                </div>

              </div>
            </button>

            <button
              type="button"
              onClick={() => setConvocatoria('evaluador')}
              className="w-full text-left rounded-2xl border border-border bg-card p-5 transition-all hover:border-secondary hover:shadow-md group"
            >
              <div className="flex items-start gap-4">

                <div className="w-11 h-11 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center flex-shrink-0 group-hover:bg-secondary group-hover:text-white transition-colors">
                  <ClipboardList size={22} />
                </div>

                <div className="flex-1">
                  <p className="font-800 text-base text-foreground">
                    Convocatoria para evaluadores
                  </p>

                  <p className="text-sm text-muted-foreground mt-1">
                    Consulta perfil, requisitos y proceso de selección.
                  </p>

                  <span className="inline-flex items-center gap-1.5 mt-3 text-xs font-700 text-secondary">
                    Consultar convocatoria
                    <ExternalLink size={13} />
                  </span>
                </div>

              </div>
            </button>

          </div>

          {/* REGISTROS */}
          <div className="space-y-4">

            <button
              type="button"
              onClick={onCompanyRegister}
              className="w-full text-left rounded-2xl border border-border bg-card p-5 transition-all hover:border-primary hover:shadow-md group"
            >
              <div className="flex items-center gap-4">

                <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-white transition-colors">
                  <Building2 size={24} />
                </div>

                <div className="flex-1">
                  <p className="font-800 text-base text-foreground">
                    Registro empresa
                  </p>

                  <p className="text-sm text-muted-foreground mt-1">
                    Completa la información de tu empresa para solicitar el registro.
                  </p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={onEvaluatorRegister}
              className="w-full text-left rounded-2xl border border-border bg-card p-5 transition-all hover:border-secondary hover:shadow-md group"
            >
              <div className="flex items-center gap-4">

                <div className="w-12 h-12 rounded-xl bg-secondary/10 text-secondary flex items-center justify-center flex-shrink-0 group-hover:bg-secondary group-hover:text-white transition-colors">
                  <UserRound size={24} />
                </div>

                <div className="flex-1">
                  <p className="font-800 text-base text-foreground">
                    Registro evaluador
                  </p>

                  <p className="text-sm text-muted-foreground mt-1">
                    Solicita tu incorporación al proceso de evaluación.
                  </p>
                </div>
              </div>
            </button>

          </div>

          {/* LOGIN */}
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>

            <div className="relative flex justify-center">
              <span className="bg-background px-4 text-xs text-muted-foreground">
                ¿Ya tienes una cuenta?
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={onLogin}
            className="w-full rounded-xl py-3 font-700 text-base flex items-center justify-center gap-2 border border-border bg-background text-foreground hover:bg-muted transition-colors"
          >
            <LogIn size={18} />
            Iniciar sesión
          </button>

          <p className="text-center text-xs text-muted-foreground/60 mt-8">
            Sistema oficial del Premio Nacional de Exportación
            <br />
            Secretaría de Economía · México
          </p>

        </div>
      </div>

      {/* MODAL DE CONVOCATORIA */}
      {convocatoria && (
        <div
          className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setConvocatoria(null)}
        >
          <div
            className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-card border border-border shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >

            {/* ENCABEZADO */}
            <div className="sticky top-0 z-10 bg-card border-b border-border px-6 py-5 flex items-start justify-between gap-4">

              <div>
                <div className="flex items-center gap-2 text-xs font-700 text-primary mb-2">
                  <BookOpen size={15} />
                  Premio Nacional de Exportación · Edición 2026
                </div>

                <h3 className="text-xl font-800 text-foreground">
                  {isCompany
                    ? 'Convocatoria para empresas'
                    : 'Convocatoria para evaluadores'}
                </h3>

                <p className="text-sm text-muted-foreground mt-1">
                  Consulta la información oficial antes de iniciar tu registro.
                </p>
              </div>

              <button
                type="button"
                onClick={() => setConvocatoria(null)}
                className="w-9 h-9 rounded-lg flex items-center justify-center hover:bg-muted text-muted-foreground"
                aria-label="Cerrar"
              >
                <X size={19} />
              </button>

            </div>

            {/* CONTENIDO */}
            <div className="p-6 space-y-4">

              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <CheckCircle2 size={19} className="text-primary" />
                  <h4 className="font-800 text-sm text-foreground">
                    Requisitos
                  </h4>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  Consulta los requisitos establecidos en la convocatoria
                  oficial correspondiente antes de presentar tu solicitud.
                </p>
              </div>

              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <ClipboardList size={19} className="text-primary" />
                  <h4 className="font-800 text-sm text-foreground">
                    Registro
                  </h4>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  El registro en esta plataforma constituye una solicitud
                  de participación y queda sujeto al proceso de revisión
                  correspondiente.
                </p>
              </div>

              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <BookOpen size={19} className="text-primary" />
                  <h4 className="font-800 text-sm text-foreground">
                    Proceso
                  </h4>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  Las etapas, condiciones y criterios aplicables serán los
                  establecidos en la convocatoria oficial.
                </p>
              </div>

              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center gap-3 mb-2">
                  <Award size={19} className="text-primary" />
                  <h4 className="font-800 text-sm text-foreground">
                    Premiación
                  </h4>
                </div>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  Consulta en la convocatoria oficial las disposiciones
                  relativas al reconocimiento y premiación.
                </p>
              </div>

              <div className="rounded-xl border border-border p-4">
                <h4 className="font-800 text-sm text-foreground mb-2">
                  Bases generales
                </h4>

                <p className="text-sm text-muted-foreground leading-relaxed">
                  La participación se encuentra sujeta a las bases y
                  disposiciones publicadas oficialmente para la edición
                  correspondiente.
                </p>
              </div>

              {/* ENLACE DOF */}
              {(
                isCompany
                  ? DOF_COMPANY_URL
                  : DOF_EVALUATOR_URL
              ) ? (
                <a
                  href={isCompany ? DOF_COMPANY_URL : DOF_EVALUATOR_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full rounded-xl py-3.5 px-4 bg-primary text-white font-700 flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
                >
                  <ExternalLink size={18} />
                  Consultar publicación oficial en el DOF
                </a>
              ) : (
                <div className="rounded-xl bg-muted px-4 py-3 text-center text-xs text-muted-foreground">
                  La publicación oficial en el DOF se habilitará en cuanto
                  se incorpore la liga correspondiente.
                </div>
              )}

              {/* REGISTRO */}
              <button
                type="button"
                onClick={() => {
                  setConvocatoria(null);

                  if (isCompany) {
                    onCompanyRegister();
                  } else {
                    onEvaluatorRegister();
                  }
                }}
                className="w-full rounded-xl py-3.5 px-4 border border-border bg-background text-foreground font-700 hover:bg-muted transition-colors"
              >
                {isCompany
                  ? 'Ir a Registro empresa'
                  : 'Ir a Registro evaluador'}
              </button>

            </div>
          </div>
        </div>
      )}

    </div>
  );
}
